import { CurrentPlan, DoseRequiredRecord } from '../../types/data';

export const calculateDoseRequirements = (
  currentPlans: CurrentPlan[]
): DoseRequiredRecord[] => {
  // Filter out disabled plans
  const enabledPlans = currentPlans.filter(plan => plan.enabled);

  return enabledPlans.map(plan => {
    // Calculate the duration in milliseconds and convert to days
    const durationMs = parseDuration(plan.duration_period);
    const durationDays = durationMs / (1000 * 60 * 60 * 24);
    
    // Calculate total doses based on frequency interval
    let totalDoses = 0;
    const dosesPerInterval = plan.doses_per_interval || 1;
    const frequencyInterval = plan.frequency_interval || '1 day';
    const frequencyIntervalMs = parseDuration(frequencyInterval);
    
    if (frequencyInterval === '1 day' && (plan.days_on || plan.days_off)) {
      // Special handling for daily frequency with on/off days
      const cycleLength = (plan.days_on || 0) + (plan.days_off || 0);
      const activeDays = plan.days_on || 0;
      
      // Calculate how many active days we have within the duration
      const totalActiveDays = Math.floor(durationDays / cycleLength) * activeDays +
        Math.min(activeDays, durationDays % cycleLength);
      
      totalDoses = totalActiveDays * dosesPerInterval;
    } else {
      // For all other frequency intervals, calculate intervals within duration
      const intervalsInDuration = Math.floor(durationMs / frequencyIntervalMs);
      totalDoses = intervalsInDuration * dosesPerInterval;
    }

    // Calculate total amount
    const totalAmount = totalDoses * (plan.dosage || 0);

    return {
      name: plan.name,
      totalAmount,
      totalAmountUnits: plan.dosage_units || 'N/A',
      totalDoses,
      startDate: plan.start_date,
      endDate: new Date(
        new Date(plan.start_date).getTime() + durationMs
      ).toISOString().split('T')[0],
    };
  });
};

const parseDuration = (duration: string): number => {
  const match = duration.match(/(\d+)\s*(\w+)/);
  if (!match) return 0;

  const [_, value, unit] = match;
  const normalizedUnit = unit.toLowerCase();
  let multiplier: number;

  switch (normalizedUnit) {
    case 'day':
    case 'days':
      multiplier = 1;
      break;
    case 'week':
    case 'weeks':
      multiplier = 7;
      break;
    case 'month':
    case 'months':
      multiplier = 30.44; // Average days in a month (365.25/12)
      break;
    default:
      throw new Error(`Unsupported duration unit: ${unit}`);
  }

  return parseInt(value, 10) * multiplier * 24 * 60 * 60 * 1000; // Convert to ms
};